import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import moment from 'moment';
import 'moment/locale/es';
import { appConstants } from '../_constants/app.constants';
import {
  applyBracketMatchShields,
  applyLeagueMatchShields,
  bracketMatchHasBothTeams,
  bracketSideLabel,
} from './mesaUtils';

const API = appConstants.API_URL;

function leagueStatus(m) {
  if (m.completed) return { label: 'Finalizado', color: 'default' };
  if (m.isActive) return { label: 'En juego', color: 'success' };
  return { label: 'Pendiente', color: 'warning' };
}

const byDayHourAsc = (a, b) => new Date(a.dayHour) - new Date(b.dayHour);

export default function MesasHubPage() {
  const { tournamentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [league, setLeague] = useState([]);
  const [finals, setFinals] = useState([]);
  const [title, setTitle] = useState('');
  const [tab, setTab] = useState(0); // 0 = Pendientes, 1 = Completados, 2 = Todos
  const [search, setSearch] = useState('');

  useEffect(() => {
    moment.locale('es');
    const cached = sessionStorage.getItem(`mesaTournamentName_${tournamentId}`);
    if (cached) setTitle(cached);
  }, [tournamentId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [lg, fin] = await Promise.all([
          axios.get(`${API}/api/tournamentmatches/${tournamentId}`),
          axios.get(`${API}/api/tournamentmatches/${tournamentId}/bracketmatchs`),
        ]);
        if (cancelled) return;
        const lgArr = Array.isArray(lg.data) ? lg.data : [];
        const finArr = Array.isArray(fin.data) ? fin.data : [];
        lgArr.sort(byDayHourAsc);
        finArr.sort(byDayHourAsc);
        setLeague(lgArr.map((m) => applyLeagueMatchShields(m, tournamentId)));
        setFinals(finArr.map((m) => applyBracketMatchShields(m, tournamentId)));
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.message || e.message || 'Error al cargar');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tournamentId]);

  // Recuentos para las pestañas (liga + fase final juntos).
  const counts = useMemo(() => {
    const all = [...league, ...finals];
    const pendientes = all.filter((m) => !m.completed).length;
    const completados = all.filter((m) => m.completed).length;
    return { pendientes, completados, todos: all.length };
  }, [league, finals]);

  // El "siguiente" partido a jugar: el no finalizado más próximo por fecha.
  const nextMatchId = useMemo(() => {
    const playableFinals = finals.filter((m) => bracketMatchHasBothTeams(m));
    const candidates = [...league, ...playableFinals]
      .filter((m) => !m.completed)
      .sort(byDayHourAsc);
    return candidates[0]?.id ?? null;
  }, [league, finals]);

  const filterForTab = (list) => {
    if (tab === 0) return list.filter((m) => !m.completed);
    if (tab === 1) return list.filter((m) => m.completed);
    return list;
  };

  const normalize = (s) => (s || '').toString().toLowerCase().trim();
  const matchesSearch = (m, kind) => {
    const q = normalize(search);
    if (!q) return true;
    const parts =
      kind === 'bracket'
        ? [bracketSideLabel(m, 'local'), bracketSideLabel(m, 'foreign'), m.name]
        : [m.localTeam?.name, m.foreignTeam?.name];
    return parts.some((p) => normalize(p).includes(q));
  };

  const leagueShown = filterForTab(league).filter((m) => matchesSearch(m, 'league'));
  const finalsShown = filterForTab(finals).filter((m) => matchesSearch(m, 'bracket'));

  return (
    <Container maxWidth="sm" sx={{ py: 2, px: 2, pb: 6 }}>
      <Button
        component={RouterLink}
        to={`/tournamentactions/${tournamentId}`}
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2, textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
      >
        Volver al torneo
      </Button>

      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
        <SportsSoccerIcon sx={{ color: '#0f6b5c', fontSize: 32 }} />
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f3531', lineHeight: 1.2 }}>
          Mesa · {title || 'Torneo'}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Pulsa un partido para marcar goles. Pensado para móvil en mesa. El organizador debe compartir solo este enlace
        con quienes marcan.
      </Typography>

      <Button
        component={RouterLink}
        to={`/tournamentactions/${tournamentId}/mesa/multiple`}
        variant="contained"
        fullWidth
        startIcon={<ViewAgendaIcon />}
        sx={{
          mb: 2,
          py: 1.25,
          fontWeight: 800,
          textTransform: 'none',
          borderRadius: 2,
          bgcolor: '#0f6b5c',
          '&:hover': { bgcolor: '#0d5a4f' },
        }}
      >
        Gestionar varios partidos a la vez
      </Button>

      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        <strong>Mesa:</strong> hace falta iniciar sesión con una cuenta invitada como <strong>LiveOperator</strong> o{' '}
        <strong>Admin</strong> del torneo. Las lecturas del calendario pueden ser públicas; al guardar marcador la API
        exige JWT válido.
      </Alert>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <>
          <Tabs
            value={tab}
            onChange={(_e, v) => setTab(v)}
            variant="fullWidth"
            sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none', fontWeight: 700 } }}
          >
            <Tab label={`Pendientes (${counts.pendientes})`} />
            <Tab label={`Completados (${counts.completados})`} />
            <Tab label={`Todos (${counts.todos})`} />
          </Tabs>

          <TextField
            fullWidth
            size="small"
            placeholder="Buscar por equipo…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5, color: '#0f3531' }}>
                Liga / grupos
              </Typography>
              {leagueShown.length === 0 ? (
                <Typography color="text.secondary">
                  {tab === 0
                    ? 'No quedan partidos de liga por jugar.'
                    : tab === 1
                    ? 'Aún no hay partidos de liga finalizados.'
                    : 'No hay partidos de liga.'}
                </Typography>
              ) : (
                <Stack spacing={1.25}>
                  {leagueShown.map((m) => {
                    const st = leagueStatus(m);
                    const t1 = m.localTeam?.name || 'Local';
                    const t2 = m.foreignTeam?.name || 'Visitante';
                    const isNext = m.id === nextMatchId;
                    return (
                      <Paper
                        key={m.id}
                        component={RouterLink}
                        to={`/tournamentactions/${tournamentId}/mesa/${m.id}`}
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: isNext ? '2px solid #0f6b5c' : '1px solid rgba(15,53,49,0.12)',
                          bgcolor: isNext ? 'rgba(15,107,92,0.05)' : '#fff',
                          textDecoration: 'none',
                          color: 'inherit',
                          display: 'block',
                          '&:active': { bgcolor: 'rgba(15,107,92,0.08)' },
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" color="text.secondary">
                              {m.fieldName ? `${m.fieldName} · ` : ''}
                              {moment(m.dayHour).format('ddd D MMM, HH:mm')}
                            </Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.5 }}>
                              {t1}{' '}
                              <Typography component="span" variant="body2" color="text.secondary">
                                vs
                              </Typography>{' '}
                              {t2}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.5, color: '#0f6b5c' }}>
                              {m.localGoals ?? 0} — {m.foreignGoals ?? 0}
                            </Typography>
                          </Box>
                          <Stack alignItems="flex-end" spacing={0.5}>
                            <Chip size="small" label={st.label} color={st.color} sx={{ fontWeight: 700 }} />
                            {isNext && (
                              <Chip
                                size="small"
                                label="Siguiente"
                                sx={{ fontWeight: 800, bgcolor: '#0f6b5c', color: '#fff' }}
                              />
                            )}
                          </Stack>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              )}
            </Box>

            <Divider />

            <Box>
              <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 1.5 }}>
                <EmojiEventsIcon sx={{ color: '#c9a227' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f3531' }}>
                  Fase final
                </Typography>
              </Stack>
              {finalsShown.length === 0 ? (
                <Typography color="text.secondary">
                  {tab === 0
                    ? 'No quedan partidos de eliminatoria por jugar.'
                    : tab === 1
                    ? 'Aún no hay partidos de eliminatoria finalizados.'
                    : 'No hay partidos de eliminatoria.'}
                </Typography>
              ) : (
                <Stack spacing={1.25}>
                  {finalsShown.map((m) => {
                    const ready = bracketMatchHasBothTeams(m);
                    const t1 = bracketSideLabel(m, 'local');
                    const t2 = bracketSideLabel(m, 'foreign');
                    const st = leagueStatus(m);
                    const isNext = m.id === nextMatchId;
                    const inner = (
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" color="text.secondary">
                            {m.fieldName ? `${m.fieldName} · ` : ''}
                            {moment(m.dayHour).format('ddd D MMM, HH:mm')}
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, mt: 0.5, color: '#0f3531' }}>
                            {m.name}
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.25 }}>
                            {t1}{' '}
                            <Typography component="span" variant="body2" color="text.secondary">
                              vs
                            </Typography>{' '}
                            {t2}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.5, color: '#8b6914' }}>
                            {m.localGoals ?? 0} — {m.foreignGoals ?? 0}
                          </Typography>
                          {!ready && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Equipos pendientes: no se puede abrir hasta que ambos bandos tengan equipo.
                            </Typography>
                          )}
                        </Box>
                        <Stack alignItems="flex-end" spacing={0.5}>
                          <Chip size="small" label={st.label} color={st.color} sx={{ fontWeight: 700 }} />
                          {isNext && (
                            <Chip
                              size="small"
                              label="Siguiente"
                              sx={{ fontWeight: 800, bgcolor: '#0f6b5c', color: '#fff' }}
                            />
                          )}
                          {!ready && (
                            <Chip
                              size="small"
                              label="Sin equipos"
                              color="default"
                              variant="outlined"
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                        </Stack>
                      </Stack>
                    );
                    if (ready) {
                      return (
                        <Paper
                          key={m.id}
                          component={RouterLink}
                          to={`/tournamentactions/${tournamentId}/mesa/final/${m.id}`}
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            border: isNext ? '2px solid #0f6b5c' : '1px solid rgba(201,162,39,0.35)',
                            bgcolor: isNext ? 'rgba(15,107,92,0.05)' : '#fff',
                            textDecoration: 'none',
                            color: 'inherit',
                            display: 'block',
                            '&:active': { bgcolor: 'rgba(201,162,39,0.12)' },
                          }}
                        >
                          {inner}
                        </Paper>
                      );
                    }
                    return (
                      <Paper
                        key={m.id}
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: '1px dashed rgba(201,162,39,0.45)',
                          color: 'inherit',
                          display: 'block',
                          bgcolor: 'rgba(201,162,39,0.04)',
                        }}
                      >
                        {inner}
                      </Paper>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Stack>
        </>
      )}
    </Container>
  );
}
